import {
  Injectable,
  inject,
  signal,
  EnvironmentInjector,
  runInInjectionContext,
  OnDestroy
} from '@angular/core';
import {
  Database,
  ref,
  onDisconnect,
  set,
  onValue,
  get,
  serverTimestamp,
  remove
} from '@angular/fire/database';
import { Auth } from '@angular/fire/auth';
import { getTabId } from '../utils/tab-id';
import { EstadisticasService } from './estadisticas.service';

type PresenceState = 'online' | 'away';
const THROTTLE_INTERVAL_MS = 300_000; // mín. 30s entre escrituras

@Injectable({ providedIn: 'root' })
export class PresenceService implements OnDestroy {
  private db = inject(Database);
  private auth = inject(Auth);
  private injector = inject(EnvironmentInjector);
  private estadisticasService = inject(EstadisticasService);
  public initialized = false;
  private lastUidInitialized: string | null = null;

  private estadoPorUsuario = new Map<string, 'online' | 'away'>();
  private unsubAuth?: () => void;
  private onlineSet = signal<Set<string>>(new Set());

  private lastWrittenState: PresenceState = 'away';
  private lastWriteTime = 0;
  private activityTimeout?: ReturnType<typeof setTimeout>;
  private readonly INACTIVITY_THRESHOLD_MS = 300_000; // 5 min

  constructor() {}

  get onlineUsers(): Set<string> {
    return this.onlineSet();
  }

  isOnline(uid: string): boolean {
    return this.onlineSet().has(uid);
  }

  getEstado(uid: string): 'online' | 'away' | 'offline' {
    return this.estadoPorUsuario.get(uid) ?? 'offline';
  }

  isDisponible(uid: string): boolean {
    const estado = this.estadoPorUsuario.get(uid);
    return estado === 'online' || estado === 'away';
  }

  async setPresence(): Promise<void> {
  const user = this.auth.currentUser;
  if (!user) return;

  const uid = user.uid;
  const tabId = getTabId();
  const tabRef = ref(this.db, `/status/${uid}/tabs/${tabId}`);
  const connectedRef = ref(this.db, '.info/connected');

  const isNewLogin = !this.initialized || this.lastUidInitialized !== uid;
  this.initialized = true;
  this.lastUidInitialized = uid;

  try {
    const tabSnap = await get(tabRef);
    const shouldForce = isNewLogin || !tabSnap.exists();

    onValue(connectedRef, async (snap) => {
      if (snap.val() === false) return;

      await this.writeState(tabRef, 'online', shouldForce);
      console.log('[PresenceService] Estado escrito:', 'online (force:', shouldForce, ')');

      onDisconnect(tabRef).remove().catch(e => {
        console.warn('[PresenceService] Error en onDisconnect:', e);
      });

      this.setupInactivityDetection(uid, tabId, tabRef);
    });
  } catch (error) {
    console.error('[PresenceService] Error en setPresence:', error);
    throw new Error('Error al establecer presencia: ' + error);
  }
}


  listenOnlineUsers() {
    const statusRef = ref(this.db, '/status');

    runInInjectionContext(this.injector, () => {
      onValue(statusRef, async (snapshot) => {
        const data = snapshot.val();
        const onlineUIDs = new Set<string>();
        const estados = new Map<string, 'online' | 'away'>();

        if (data) {
          for (const uid of Object.keys(data)) {
            const tabs = data[uid]?.tabs;
            let tieneOnline = false;
            let tieneAway = false;

            for (const tab of Object.values(tabs ?? {}) as Array<{ state: string }>) {
              if (tab.state === 'online') tieneOnline = true;
              if (tab.state === 'away') tieneAway = true;
            }

            if (tieneOnline) {
              onlineUIDs.add(uid);
              estados.set(uid, 'online');
            } else if (tieneAway) {
              estados.set(uid, 'away');
            }
          }
        }

        this.onlineSet.set(onlineUIDs);
        this.estadoPorUsuario = estados;

        await this.estadisticasService.setearCampo('usuariosOnlineActualmente', onlineUIDs.size);
      });
    });
  }

  private setupInactivityDetection(
    uid: string,
    tabId: string,
    tabRef: ReturnType<typeof ref>
  ): void {
    const resetTimer = async () => {
      clearTimeout(this.activityTimeout);
      await this.writeState(tabRef, 'online');

      this.activityTimeout = setTimeout(async () => {
        await this.writeState(tabRef, 'away');
      }, this.INACTIVITY_THRESHOLD_MS);
    };

    ['mousemove', 'keydown', 'scroll', 'click'].forEach((event) =>
      window.addEventListener(event, resetTimer)
    );

    resetTimer();
  }

  private async writeState(
  tabRef: ReturnType<typeof ref>,
  state: PresenceState,
  force = false
): Promise<void> {
  const now = Date.now();

  // Si no hay cambios relevantes y no es forzado, no escribimos
  if (
    !force &&
    this.lastWrittenState === state &&
    now - this.lastWriteTime < THROTTLE_INTERVAL_MS
  ) {
    return;
  }

  this.lastWrittenState = state;
  this.lastWriteTime = now;

  try {
    await set(tabRef, {
      state,
      last_seen: serverTimestamp()
    });
  } catch (error) {
    console.error('[PresenceService] Error al escribir presencia:', error);
  }
}


  async clearPresence(): Promise<void> {
    this.initialized = false;
    this.lastUidInitialized = null;
    clearTimeout(this.activityTimeout);

    const user = this.auth.currentUser;
    if (!user) return;

    const uid = user.uid;
    const tabId = getTabId();
    const tabRef = ref(this.db, `/status/${uid}/tabs/${tabId}`);

    if (this.unsubAuth) this.unsubAuth();
    await remove(tabRef);
  }

  async getEstadoPresencia(uid: string): Promise<'online' | 'away' | 'offline'> {
    const snap = await get(ref(this.db, `/status/${uid}/tabs`));
    if (!snap.exists()) return 'offline';

    const tabs = snap.val();
    const states = Object.values(tabs).map((t: any) => t.state);

    if (states.includes('online')) return 'online';
    if (states.includes('away')) return 'away';
    return 'offline';
  }

  ngOnDestroy(): void {
    if (this.unsubAuth) this.unsubAuth();
  }
}
